import { useState, useRef, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';

type PopoverPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end';
type PopoverTrigger = 'click' | 'hover';

export type PopoverProps = {
  children: ReactNode;
  content: ReactNode;
  position?: PopoverPosition;
  trigger?: PopoverTrigger;
  showArrow?: boolean;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  closeOnClickOutside?: boolean;
  className?: string;
  contentClassName?: string;
};

const positionStyles: Record<PopoverPosition, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  'top-start': 'bottom-full left-0 mb-2',
  'top-end': 'bottom-full right-0 mb-2',
  'bottom-start': 'top-full left-0 mt-2',
  'bottom-end': 'top-full right-0 mt-2',
};

const arrowPositionStyles: Record<PopoverPosition, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-white dark:border-t-dark-700 border-x-transparent border-b-transparent',
  bottom:
    'bottom-full left-1/2 -translate-x-1/2 border-b-white dark:border-b-dark-700 border-x-transparent border-t-transparent',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-white dark:border-l-dark-700 border-y-transparent border-r-transparent',
  right:
    'right-full top-1/2 -translate-y-1/2 border-r-white dark:border-r-dark-700 border-y-transparent border-l-transparent',
  'top-start':
    'top-full left-4 border-t-white dark:border-t-dark-700 border-x-transparent border-b-transparent',
  'top-end':
    'top-full right-4 border-t-white dark:border-t-dark-700 border-x-transparent border-b-transparent',
  'bottom-start':
    'bottom-full left-4 border-b-white dark:border-b-dark-700 border-x-transparent border-t-transparent',
  'bottom-end':
    'bottom-full right-4 border-b-white dark:border-b-dark-700 border-x-transparent border-t-transparent',
};

function Popover({
  children,
  content,
  position = 'bottom',
  trigger = 'click',
  showArrow = true,
  isOpen: controlledIsOpen,
  onOpenChange,
  closeOnClickOutside = true,
  className,
  contentClassName,
}: PopoverProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = useCallback(
    (value: boolean) => {
      if (!isControlled) {
        setInternalIsOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const handleTriggerClick = () => {
    if (trigger === 'click') {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!closeOnClickOutside || !isOpen) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeOnClickOutside, isOpen, setIsOpen]);

  return (
    <div
      ref={popoverRef}
      className={clsx('relative inline-flex', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div onClick={handleTriggerClick}>{children}</div>
      {isOpen && (
        <div
          className={clsx(
            'absolute z-50',
            positionStyles[position],
            'animate-in fade-in zoom-in-95 duration-150',
            contentClassName
          )}
        >
          <div className="relative bg-white dark:bg-dark-700 rounded-lg shadow-lg border border-gray-200 dark:border-dark-600 p-3">
            {content}
            {showArrow && (
              <span
                className={clsx('absolute w-0 h-0 border-[6px]', arrowPositionStyles[position])}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

Popover.displayName = 'Popover';

export { Popover };
